import { createAsyncThunk } from '@reduxjs/toolkit'
import * as bookingsApi from '../../api/bookings.api'
import type { CreateBookingRequest, CreateUserBookingRequest, UpdateBookingRequest } from '../../types/booking.types'
import type { TableType } from '../../types/table.types'

export const fetchAvailabilityGrid = createAsyncThunk(
    'bookings/fetchAvailabilityGrid',
    async (
        params: { startDate?: string; tableType?: TableType | null },
        { rejectWithValue }
    ) => {
        try {
            return await bookingsApi.fetchAvailabilityGrid(params)
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to load availability'
            return rejectWithValue(message)
        }
    }
)

export const fetchBookingsList = createAsyncThunk(
    'bookings/fetchBookingsList',
    async (
        params: { from?: string; to?: string; status?: string },
        { rejectWithValue }
    ) => {
        try {
            return await bookingsApi.fetchBookings(params)
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to load bookings'
            return rejectWithValue(message)
        }
    }
)

export const createBookingThunk = createAsyncThunk(
    'bookings/createBooking',
    async (request: CreateBookingRequest, { rejectWithValue }) => {
        try {
            return await bookingsApi.createBooking(request)
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to create booking'
            return rejectWithValue(message)
        }
    }
)

export const cancelBookingThunk = createAsyncThunk(
    'bookings/cancelBooking',
    async (bookingId: number, { rejectWithValue }) => {
        try {
            await bookingsApi.cancelBooking(bookingId)
            return bookingId
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to cancel booking'
            return rejectWithValue(message)
        }
    }
)

export const updateBookingThunk = createAsyncThunk(
    'bookings/updateBooking',
    async (
        params: { id: number } & UpdateBookingRequest,
        { rejectWithValue }
    ) => {
        try {
            const { id, ...request } = params
            return await bookingsApi.updateBooking(id, request)
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to update booking'
            return rejectWithValue(message)
        }
    }
)

export const fetchMyBookings = createAsyncThunk(
    'bookings/fetchMyBookings',
    async (_, { rejectWithValue }) => {
        try {
            return await bookingsApi.fetchMyBookings()
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to load your bookings'
            return rejectWithValue(message)
        }
    }
)

export const fetchMyBookingSettings = createAsyncThunk(
    'bookings/fetchMyBookingSettings',
    async (_, { rejectWithValue }) => {
        try {
            return await bookingsApi.fetchMyBookingSettings()
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to load booking settings'
            return rejectWithValue(message)
        }
    }
)

export const createMyBookingThunk = createAsyncThunk(
    'bookings/createMyBooking',
    async (request: CreateUserBookingRequest, { rejectWithValue }) => {
        try {
            return await bookingsApi.createMyBooking(request)
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to create booking'
            return rejectWithValue(message)
        }
    }
)

export const cancelMyBookingThunk = createAsyncThunk(
    'bookings/cancelMyBooking',
    async (bookingId: number, { rejectWithValue }) => {
        try {
            await bookingsApi.cancelMyBooking(bookingId)
            return bookingId
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to cancel booking'
            return rejectWithValue(message)
        }
    }
)
